import { useCallback } from 'react';
import { formatDateToISOutc, toLocalMoment } from 'utils';
import {
  addMs,
  resolveEndOnEndChange,
  resolveEndOnStartChange,
} from './use-date-range.utils';
import { UseDateRangeParams } from './use-date-range.types';
import {
  DEFAULT_DURATION,
  DEFAULT_PICKER_MIN_TIME_OFFSET,
} from './use-date-range.constants';

/**
 * Хук для управления диапазоном дат с автоматической коррекцией значений.
 *
 * Берёт на себя:
 * - парсинг ISO-строк в Date-объекты для датепикеров
 * - автоматическую установку end при выборе start (если передан `defaultDuration`)
 * - клампинг end в пределах `minDuration`/`maxDuration` относительно start
 * - защиту от инверсии (end < start)
 * - вычисление `minTime`/`maxTime` для end-пикера
 *
 * @example
 * const { startDate, endDate, endMinTime, endMaxTime, handleChangeStart, handleChangeEnd } =
 *   useDateRange({
 *     value: { start: requests.currentOnEdit.execution.desired_start_at, end: requests.currentOnEdit.execution.desired_start_at },
 *     onChange: (next: DateRangeFields) => handleChange(next),
 *     defaultDuration: 30 * 60 * 1000, // автоставить end через 30 мин
 *     minDuration: 30 * 60 * 1000,     // нельзя поставить end раньше чем через 30 мин
 *     maxDuration: 8 * 60 * 60 * 1000, // нельзя поставить end позже чем через 8 часов
 *   });
 */
export const useDateRange = ({
  value,
  onChange,
  defaultDuration,
  minDuration = DEFAULT_DURATION,
  maxDuration,
  pickerMinTimeOffset = DEFAULT_PICKER_MIN_TIME_OFFSET,
}: UseDateRangeParams) => {
  const start = value?.start;
  const end = value?.end;

  const startDate = start ? toLocalMoment(start).toDate() : null;

  const endDate = (() => {
    if (!end) {
      return null;
    }

    if (start && toLocalMoment(start).isAfter(toLocalMoment(end))) {
      return toLocalMoment(start).toDate();
    }

    return toLocalMoment(end).toDate();
  })();

  const endMinTime = (() => {
    if (!startDate) {
      return undefined;
    }

    if (endDate && endDate.toDateString() === startDate.toDateString()) {
      return addMs(startDate, pickerMinTimeOffset);
    }

    return new Date(
      startDate.getFullYear(),
      startDate.getMonth(),
      startDate.getDate(),
      0,
      0
    );
  })();

  const endMaxTime = startDate
    ? new Date(
        startDate.getFullYear(),
        startDate.getMonth(),
        startDate.getDate(),
        23,
        59
      )
    : undefined;

  const handleChangeStart = useCallback(
    (date: Date | null) => {
      onChange({
        ...value,
        start: date ? formatDateToISOutc(date) : null,
        end: resolveEndOnStartChange(
          date,
          end,
          defaultDuration,
          minDuration,
          maxDuration
        ),
      });
    },
    [value, end, defaultDuration, minDuration, maxDuration, onChange]
  );

  const handleChangeEnd = useCallback(
    (date: Date | null) => {
      const resolved = resolveEndOnEndChange(
        date,
        start,
        minDuration,
        maxDuration
      );
      onChange({
        ...value,
        end: resolved ? formatDateToISOutc(resolved) : null,
      });
    },
    [value, start, minDuration, maxDuration, onChange]
  );

  return {
    startDate,
    endDate,
    endMinTime,
    endMaxTime,
    handleChangeStart,
    handleChangeEnd,
  };
};
